/**
 * @component
 * @name ActionSubmitted
 *
 *
 * @param {object} props - Props for the ActionSubmitted component.
 * @param {("FEEDBACK_GIVEN" | "APPROVED" | "REJECTED" | "EXPIRED" | "IN_REVIEW")} [props.reviewStatus] - Specifies the review status.
 *
 * @returns {JSX.Element} React component for displaying review action status.
 *
 * @description Overview:
 * The `ActionSubmitted` component is responsible for rendering the status of a review action based on the provided review status.
 * It accepts `reviewStatus` as a prop, which can be one of the following values:
 * - "FEEDBACK_GIVEN"
 * - "APPROVED"
 * - "REJECTED"
 * - "EXPIRED"
 * - "IN_REVIEW"
 * The component displays different messages based on the review status according to reviewer's submitted action.
 *
 * @see {@link ReviewMethodologyComponent} - Component is used here.
 *
 *
 * @example
 * // Example usage of ActionSubmitted component
 * <ActionSubmitted reviewStatus="APPROVED" />

 */
interface IProps {
	reviewStatus?: "FEEDBACK_GIVEN" | "APPROVED" | "REJECTED" | "EXPIRED" | "IN_REVIEW" | "ABSENT" | "IN_ACTIVE";
}

type ActionMessageMap = Partial<Record<NonNullable<IProps["reviewStatus"]>, string>>;

const ActionSubmitted = (props: IProps) => {
	const reviewStatus = props.reviewStatus;

	const actionMessageMap: ActionMessageMap = {
		EXPIRED: "You did not perform any actions on this round",
		ABSENT: "You were absent in this methodology for this round",
		IN_ACTIVE: "You are not authorized to perform any actions in this methodology for this round",
		APPROVED: "You have approved this methodology for this round",
		REJECTED: "You have rejected this methodology for this round",
	};

	const bgColorClassMap: ActionMessageMap = {
		APPROVED: "bg-primary bg-opacity-30",
		REJECTED: "bg-danger bg-opacity-30",
	};

	if (!reviewStatus || !actionMessageMap[reviewStatus]) {
		return null;
	}

	return (
		<>
			<p
				className={`mb-6 max-w-2xl rounded-lg px-4 py-6 text-sm text-gray-500 ${
					bgColorClassMap[reviewStatus] || "bg-sand"
				}`}
			>
				{actionMessageMap[reviewStatus]}
			</p>
		</>
	);
};

export default ActionSubmitted;
